import { HttpClient, HttpClientDefaults } from "./core/HttpClient";
import { HttpError } from "./core/HttpError";
import {
  HttpClientManager,
  HttpClientManagerDefaults,
} from "./core/HttpClientManager";
import { forEachHttpClient } from "./utils";
import { inject, App } from "vue";
import { getCustomConfig } from "./core/customConfig";

let httpClientKey = "httpClient";
const clientManager = new HttpClientManager();

const createClientManager = (defaultConfig?: HttpClientManagerDefaults) => {
  if (defaultConfig) {
    clientManager.defaultConfig = defaultConfig;
  }

  return clientManager;
};

const createClient = <CUSTOM_CONFIG extends Record<string, any>>(
  config?: HttpClientDefaults
) => {
  return new HttpClient<CUSTOM_CONFIG>(config);
};

const bindHttpClients = <
  CUSTOM_CONFIG extends Record<string, any>,
  CLIENT extends HttpClient<CUSTOM_CONFIG>,
  CLIENT_SET extends Record<string, CLIENT>
>(
  httpClientManager: HttpClientManager,
  httpClients: CLIENT_SET,
  clientKey: string = httpClientKey
) => {
  httpClientKey = clientKey;

  forEachHttpClient(httpClients, (client) => {
    httpClientManager.register(client);
  });

  const newClients: {
    [K in keyof CLIENT_SET]: Pick<
      CLIENT_SET[K],
      | "client"
      | "request"
      | "get"
      | "delete"
      | "head"
      | "options"
      | "post"
      | "put"
      | "patch"
    >;
  } = { ...httpClients };
  forEachHttpClient(newClients, (client, k) => {
    newClients[k] = {
      client: client.client,
      request: client.request,
      get: client.get,
      delete: client.delete,
      head: client.head,
      options: client.options,
      post: client.post,
      put: client.put,
      patch: client.patch,
    };
  });

  return {
    install(app: App, option?: { injectedKey: string }) {
      const key = option?.injectedKey || clientKey;

      app.provide(key, newClients);
      app.config.globalProperties[`$${key}`] = newClients;
    },
    clients: newClients,
  };
};

const useHttpClient = <T>() => {
  const clients = inject<T>(httpClientKey);

  if (!clients) {
    throw new Error("Please install http client first.");
  }

  return clients;
};

export {
  HttpError,
  createClientManager,
  createClient,
  bindHttpClients,
  useHttpClient,
  getCustomConfig,
};
